import { useBreakpointValue } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { QueryFilters } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { XAxisMode } from '@resistapp/common/types';
import { useMemo } from 'react';
import { FilterBarResistomapSelect as ResistomapSelect } from '../../forms/filter-bar-resistomap-select';

export type Props = { queryFilters: QueryFilters; compact?: boolean };

interface XAxisModeOption {
  label: string;
  value: XAxisMode;
}

function getShortXAxisLabel(mode: XAxisMode, isWideScreen: boolean): string {
  switch (mode) {
    case XAxisMode.TIME:
      return isWideScreen ? 'Against time' : 'Time';
    case XAxisMode.ORDINAL_GROUPED:
      return isWideScreen ? 'Replicate groups' : 'Grouped';
    case XAxisMode.ORDINAL_INDIVIDUAL:
      return isWideScreen ? 'Individual samples' : 'Individual';
    default:
      return 'Unknown';
  }
}

export function XAxisModeSelector({ queryFilters, compact = false }: Props) {
  // Screen size aware label selection similar to NormalisationModeSelector.
  const screenSize = useBreakpointValue({ base: 'base', md: 'md', lg: 'lg', lg2: 'lg2', xl: 'xl' });
  const isWideScreen = screenSize === 'xl' || screenSize === 'lg2' || screenSize === 'md';
  const isWide = compact ? false : isWideScreen;

  const { availableXAxisModes } = useSampleDataContext();

  const options: XAxisModeOption[] = useMemo(
    () =>
      availableXAxisModes.map(mode => ({
        label: getShortXAxisLabel(mode, isWide),
        value: mode,
      })),
    [availableXAxisModes, isWide],
  );

  const selectedOption = options.find(option => option.value === queryFilters.filters.xAxisMode) || options[0];

  return (
    <Container>
      <ResistomapSelect<XAxisModeOption, false>
        options={options}
        value={selectedOption}
        onChange={option => {
          if (option?.value) {
            queryFilters.setXAxisModeStable(option.value);
          }
        }}
        size="md"
      />
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
`;
